import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectSelectedTakeActors } from "../../app/slices/TrainingSlice";
import { PiChipNeutralScope } from "../../components/PiChip";
import PiUnderlineTabs, { piUnderlineTabId, piUnderlineTabPanelId } from "../../components/PiUnderlineTabs";
import { Actor } from "../../models/Actor";
import { ActorMovementType } from "../../models/ActorMovementType";
import { ActorState, ActorStateAnimation } from "../../models/ActorState";
import OutfitPickerField from "../outfit_picker/OutfitPickerField";
import ActorStateAnimationSelector from "./ActorStateAnimationSelector";
import ActorStateAttachmentRows from "./ActorStateAttachmentRows";
import useActorStateAttachments from "./useActorStateAttachments";
import { isNoLookat } from "../../models/NoLookat";

export type ActorSettingsTab = "general" | "appearance" | "attachments";

const TAB_STORAGE_KEY = "actorSettingsTab";
const TABS: ActorSettingsTab[] = ["general", "appearance", "attachments"];
const ID_PREFIX = "actor-settings";

const MOVEMENT_KEYS: Record<ActorMovementType, string> = {
  [ActorMovementType.Instant]: "instant",
  [ActorMovementType.Walk]: "walk",
  [ActorMovementType.AutoWalk]: "auto_walk",
  [ActorMovementType.Explore]: "explore",
};

// The last tab used opens next time, so dressing several actors in a row stays on Appearance.
function readStoredTab(): ActorSettingsTab {
  try {
    const stored = localStorage.getItem(TAB_STORAGE_KEY);
    return TABS.includes(stored as ActorSettingsTab) ? (stored as ActorSettingsTab) : "general";
  } catch {
    return "general";
  }
}

function storeTab(tab: ActorSettingsTab) {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch {
    // storage unavailable: the tab just isn't remembered
  }
}

/**
 * The take actor settings popup body: General / Appearance / Attachments tabs,
 * each with a one-line summary (Design Studio actor-settings-layout/d-*).
 * Controls are neutral grey; blue is kept for state.
 */
export default function ActorStateSettingsTabs(props: {
  actorState: ActorState;
  actor?: Actor;
  /** The General tab's settings rows. */
  general: ReactNode;
  onChangeOutfit: (outfit: string) => void;
  onChangeAnimation: (animation: ActorStateAnimation) => void;
}) {
  const { actorState, actor, general, onChangeOutfit, onChangeAnimation } = props;
  const { t } = useTranslation();
  const takeActors = useSelector(selectSelectedTakeActors);
  const { attachedProps } = useActorStateAttachments(actorState);
  const [tab, setTab] = useState<ActorSettingsTab>(readStoredTab);

  const onChangeTab = (next: ActorSettingsTab) => {
    setTab(next);
    storeTab(next);
  };

  const lookatName = actorState.lookat
    ? takeActors.find((candidate) => candidate.id === actorState.lookat)?.workingName
    : undefined;
  const generalSummary = [
    String(t(MOVEMENT_KEYS[actorState.movement] ?? "instant")),
    lookatName
      ? String(t("actor_settings.summary_looks_at", { name: lookatName }))
      : isNoLookat(actorState.lookat)
        ? String(t("actor_settings.summary_looks_at_no_one"))
        : String(t("actor_settings.summary_looks_at_keep_current")),
  ].join(" · ");

  const animationName = actorState.animation.stateName?.trim() || String(t("actor_settings.idle"));
  const outfitName = actorState.outfit || actor?.outfit || String(t("actor_settings.summary_random_outfit"));
  const appearanceSummary = `${animationName} · ${outfitName}`;

  const attachmentsSummary = attachedProps.length
    ? String(t("actor_settings.summary_props", { count: attachedProps.length }))
    : String(t("actor_settings.summary_no_props"));

  const panel = (id: ActorSettingsTab, className: string, children: ReactNode) => (
    <div
      role="tabpanel"
      id={piUnderlineTabPanelId(ID_PREFIX, id)}
      aria-labelledby={piUnderlineTabId(ID_PREFIX, id)}
      className={className}
      data-testid={`${ID_PREFIX}-panel-${id}`}
    >
      {children}
    </div>
  );

  return (
    <PiChipNeutralScope>
      <div className="flex h-full min-h-0 flex-col" data-testid="actor-state-settings">
        <PiUnderlineTabs
          ariaLabel={String(t("actor_settings.tabs_label"))}
          idPrefix={ID_PREFIX}
          value={tab}
          onChange={onChangeTab}
          tabs={[
            { id: "general", label: String(t("actor_settings.tab_general")), summary: generalSummary },
            { id: "appearance", label: String(t("actor_settings.tab_appearance")), summary: appearanceSummary },
            { id: "attachments", label: String(t("actor_settings.attachments")), summary: attachmentsSummary },
          ]}
        />

        {tab === "general" && panel("general", "min-h-0 max-w-[560px] flex-1 overflow-y-auto pt-[16px]", general)}

        {tab === "appearance" &&
          panel(
            "appearance",
            "grid min-h-0 flex-1 grid-cols-2 gap-x-[32px] pt-[16px]",
            <>
              <div className="flex min-h-0 flex-col">
                <div className="flex-none pb-[12px] text-[13px] text-creator-text-sub">
                  {String(t("actor_settings.animation"))}
                </div>
                <ActorStateAnimationSelector actorState={actorState} onChange={onChangeAnimation} variant="stage" />
              </div>
              <div className="flex min-h-0 flex-col">
                <div className="flex-none pb-[12px] text-[13px] text-creator-text-sub">
                  {String(t("actor_settings.outfit"))}
                </div>
                <OutfitPickerField
                  context="take"
                  variant="stage"
                  actor={actor}
                  value={actorState.outfit ?? ""}
                  onChange={onChangeOutfit}
                />
              </div>
            </>
          )}

        {tab === "attachments" &&
          panel(
            "attachments",
            "flex min-h-0 flex-1 flex-col gap-[12px] pt-[16px]",
            <>
              <span className="flex-none text-[12px] text-creator-text-sub">
                {String(t("actor_settings.attachments_hint"))}
              </span>
              <ActorStateAttachmentRows actorState={actorState} />
            </>
          )}
      </div>
    </PiChipNeutralScope>
  );
}
